import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import { Box, IconButton, Tooltip } from "@mui/material";
import { useMemo, useState } from "react";
import { CustomSelectScroll } from "@/components/CustomSelectScroll";
import Flex from "@/components/Flex";
import {
  REFERRING_AGENCY_ADD_ROLES,
  REFERRING_AGENCY_TITLE,
} from "@/constants/referring-agency-constant";
import { useDebounce } from "@/hooks/use-debounce";
import useDisclosure from "@/hooks/use-disclosure";
import {
  useReferringAgencyById,
  useReferringAgencyList,
} from "@/queries/use-referring-agency";
import { getBaseRole } from "@/utils/auth-storage";
import { formatReferringAgencyLabel } from "@/utils/referring-agency-utils";
import { DEF_PAGINATION, DGT_Pagination } from "@/utils/utils";
import UpsertReferringAgencyDrawer from "./UpsertReferringAgencyDrawer";

export interface ReferringAgencySelectProps {
  value: string | null;
  onChange: (agencyUuid: string | null) => void;
}

const ReferringAgencySelect = ({
  value,
  onChange,
}: ReferringAgencySelectProps) => {
  const addAgencyDrawer = useDisclosure(false);
  const [searchKey, setSearchKey] = useDebounce("");
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const { data, isFetching } = useReferringAgencyList({
    pagination,
    isArchived: false,
    isActive: true, // new
    searchKey,
  });
  const { data: selectedAgency } = useReferringAgencyById(value ?? undefined);
  const canAddAgency = REFERRING_AGENCY_ADD_ROLES.includes(getBaseRole() ?? "");

  const options = useMemo(() => {
    const agencies = data?.data?.content ?? [];
    const isSelectedListed = agencies.some((agency) => agency.uuid === value);
    const visibleAgencies =
      selectedAgency && !isSelectedListed
        ? [selectedAgency, ...agencies]
        : agencies;
    return visibleAgencies.map((agency) => ({
      label: formatReferringAgencyLabel(agency),
      value: agency.uuid,
    }));
  }, [data, selectedAgency, value]);

  return (
    <Flex alignItems="flex-end" gap={0.5}>
      <Box flex={1}>
        <CustomSelectScroll
          label="Referring Agency"
          placeholder="Select Referring Agency"
          options={options}
          value={value}
          onChange={(selectedValue) => onChange(selectedValue)}
          onSearch={(_, searchText) => {
            setSearchKey(searchText);
            setPagination((p) => ({ ...p, page: 0 }));
          }}
          enableFilterOption
          pagination={pagination}
          setPagination={setPagination}
          totalElements={data?.data?.page?.totalElements}
          loading={isFetching}
        />
      </Box>
      {canAddAgency && (
        <Tooltip title={REFERRING_AGENCY_TITLE.ADD}>
          <IconButton
            size="small"
            color="primary"
            aria-label={REFERRING_AGENCY_TITLE.ADD}
            onClick={() => addAgencyDrawer.open()}
            sx={{ mb: 0.5 }}
          >
            <AddBusinessIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {addAgencyDrawer.opened && (
        <UpsertReferringAgencyDrawer
          open={addAgencyDrawer.opened}
          onClose={addAgencyDrawer.close}
          onSave={(agency) => onChange(agency?.uuid ?? null)}
        />
      )}
    </Flex>
  );
};

export default ReferringAgencySelect;
